import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Tooltip from '../atoms/Tooltip';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import VisibilityIcon from '@material-ui/icons/VisibilityOutlined';
import SettingsIcon from '@material-ui/icons/SettingsOutlined';
import ProgressLine from '../atoms/ProgressLine';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import {
  COLOR_BACKGROUND_WARM,
  COLOR_BORDER,
  COLOR_GREEN,
  COLOR_MIDDLE_GRAY,
  COLOR_ORANGE,
  COLOR_WHITE,
} from '../styles/colors';
import { PlayerValuationProfileFactors, Valuations, PlayerFactors } from '../types/PlayerValuationProfileFactors';
import TFPillLogoColorIcon from '../icons/TFPillLogoColorIcon';
import Team from '../types/Team';
import TFVLogoIcon from '../icons/TFVLogoIcon';

interface PlayerValuationsProps {
  className?: string;
  data?: PlayerValuationProfileFactors | null;
  evaluationTeam?: Team | null;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    border: `1px solid ${COLOR_BACKGROUND_WARM}`,
  },
  headerBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    background: COLOR_BACKGROUND_WARM,
    borderBottom: `1px solid ${COLOR_BORDER}`,
    fontFamily: FONT_PROXIMA_NOVA,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    fontWeight: 700,
  },
  tfvBadge: {
    height: 22,
    padding: theme.spacing(0.25, 1),
    borderRadius: 4,
    background: COLOR_ORANGE,
    color: COLOR_WHITE,
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tfvIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
      
    '& svg': {
      width: '100%',
      height: 'auto',
    },
  },
  headerRight: {
    minWidth: 100,
  },
  select: {
    fontSize: theme.typography.pxToRem(12),
  },
  summaryRow: {
    borderBottom: `1px solid ${COLOR_BORDER}`,
    padding: theme.spacing(2, 2),
    background: COLOR_WHITE,
  },
  summaryRowInner: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: theme.spacing(1),
    },
  },
  summaryBlock: {
    flex: '1 1 200px',
    minWidth: 160,
    [theme.breakpoints.down('sm')]: {
      flex: '1 1 100%',
      minWidth: 0,
    },
  },
  summaryIconsCell: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'flex-start',
    },
  },
  iconCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(0, 4),
    flex: '0 0 auto',

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'row',
      justifyContent: 'flex-start',
      padding: 0,
      gap: theme.spacing(1),
    },

    '&:first-child': {
      paddingLeft: 0,
    },

    '&:last-child': {
      paddingRight: 0,
      borderRight: 0,
    },
  },
  divider: {
      width: 1,
      alignSelf: 'stretch',
      backgroundColor: COLOR_BORDER,
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
  },
  summaryValueCell: {
    flex: '0 0 auto',
    minWidth: 120,
    textAlign: 'right',
    [theme.breakpoints.down('sm')]: {
      minWidth: 0,
      textAlign: 'left',
    },
  },
  summaryTitle: {
    fontFamily: FONT_PROXIMA_NOVA,
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
  summarySubtitle: {
    fontSize: theme.typography.pxToRem(11),
    fontWeight: 400,
    marginBottom: theme.spacing(1),
  },
  summaryValueRow: {
    display: 'flex',
    flexDirection: 'column',
  },
  eyeIcons: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  summaryValue: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 700,
    color: COLOR_GREEN,
  },
  miniScore: {
    fontWeight: 700,
    marginRight: theme.spacing(1),
  },
  miniLogo: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    background: COLOR_ORANGE,
    marginLeft: theme.spacing(1),
  },
  tfIcon: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',

    '& svg': {
      width: 'auto',
      maxWidth: '44px',
      height: 'auto',
      maxHeight: '26px',
    },
  },
  factorsHeader: {
    padding: theme.spacing(1.5, 2),
    background: COLOR_BACKGROUND_WARM,
    borderBottom: `1px solid ${COLOR_BORDER}`,
    fontFamily: FONT_PROXIMA_NOVA,
    fontWeight: 700,
  },
  factorsRow: {
    // padding: theme.spacing(1, 2, 2),
  },
  factorGrid: {
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  factorCol: {
    [theme.breakpoints.up('md')]: {
      borderRight: `1px solid ${COLOR_BORDER}`,
      '&:last-child': {
        borderRight: 'none',
      },
    },
    [theme.breakpoints.down('sm')]: {
      borderRight: 'none',
      borderBottom: `1px solid ${COLOR_BORDER}`,
      '&:last-child': {
        borderBottom: 'none',
      },
      paddingBottom: theme.spacing(1.5),
      marginBottom: theme.spacing(1.5),
    },
    paddingRight: 0,
  },
  factorRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1.25),
    paddingTop: theme.spacing(1.25),
    paddingBottom: theme.spacing(1.25),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  factorRowOdd: {
    backgroundColor: COLOR_WHITE,
  },
  factorRowEven: {
    backgroundColor: COLOR_BACKGROUND_WARM,
  },
  factorName: {
    width: 90,
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 700,
  },
  factorInfoIcon: {
    fontSize: 20,
    // color: COLOR_MIDDLE_GRAY,
    marginRight: theme.spacing(1),
  },
  factorProgress: {
    flex: 1,
    marginRight: theme.spacing(1),
  },
  factorScore: {
    width: 30,
    textAlign: 'right',
    fontSize: theme.typography.pxToRem(16),
  },
  progressBar:{
    background: COLOR_ORANGE,
  }
}), { name: 'PlayerValuations' });

const FACTOR_COLUMNS: Array<Array<{ key: string; label: string }>> = [
  [
    { key: 'ppi', label: 'PPI' },
    { key: 'ppiRec', label: 'PPI Rec.' },
    { key: 'pff', label: 'PFF' },
    { key: 'pffRec', label: 'PFF Rec.' },
    { key: 'pai', label: 'PAI' },
    { key: 'speedPercentile', label: 'Spd %' },
    { key: 'powerPercentile', label: 'Pwr %' },
    { key: 'ms', label: 'MS' },
  ],
  [
    { key: 'heightPercentile', label: 'H %' },
    { key: 'weightPercentile', label: 'W %' },
    { key: 'expLvl', label: 'Exp. Lvl' },
    { key: 'gmExp', label: 'Gm Exp.' },
    { key: '247', label: '247' },
    { key: 'portal', label: 'Portal' },
    { key: 'eligibility', label: 'Elig.' },
    { key: 'tenure', label: 'Tenure' },
  ],
  [
    { key: 'allAmerican', label: 'All-Amer.' },
    { key: 'allConference', label: 'All-Conf.' },
    { key: 'offerCount', label: 'Offer #' },
    { key: 'offerQuality', label: 'Offer Qual.' },
    { key: 'on3PublicNILValuation', label: 'On3 Val.' },
    { key: 'on3RosterValue', label: 'On3 RV' },
    { key: 'on3PrivateNILValue', label: 'On3 NV' },
    { key: 'commitStr', label: 'Commit Str.' },
    { key: 'hsAvail', label: 'HS Avail.' },
  ],
];

function getValuationByName(
  valuations: Valuations[],
  name: string,
): { valuation: number; score: number; description: string } {
  const found = valuations.find((item) => item.name === name);
  if (!found) {
    return { valuation: 0, score: 0, description: '' };
  }
  

  return {
    valuation: found.playerValuation,
    score: found.playerValuationScore,
    description: found.description || '',
  };
}

function buildFactorMap(factors: PlayerFactors[]): Record<string, number> {
  return factors.reduce<Record<string, number>>((acc, item) => {
    acc[item.shortName] = item.playerFactorValue;
    return acc;
  }, {});
}

function buildFactorDescriptionMap(factors: PlayerFactors[]): Record<string, string> {
  return factors.reduce<Record<string, string>>((acc, item) => {
    acc[item.shortName] = item.description || '';
    return acc;
  }, {});
}

export default function PlayerValuations(props: PlayerValuationsProps) {
  const { className, data,evaluationTeam  } = props;
  const classes = useStyles();

  // const data = mockApiResponse; // Using mock API response
  const factorMap = buildFactorMap(data?.playerFactors || []);
  const factorDescriptionMap = buildFactorDescriptionMap(data?.playerFactors || []);
  const teamValuation = getValuationByName(data?.valuations || [], "Drew's Test Valuation");
  const tfCollegeValuation = getValuationByName(data?.valuations || [], 'TF College Valuation');

  return (
    <div className={clsx(classes.root, className)}>
      <div className={classes.headerBar}>
        <div className={classes.headerLeft}>
          <TFVLogoIcon className={classes.tfvIcon} />
          <span>Active Valuations</span>
        </div>
        <Select
          className={classes.headerRight}
          // value={data?.positionCode}
          value={'QB'}
          inputProps={{ className: classes.select }}
          disableUnderline
        >
          {/* <MenuItem value={data?.positionCode}>{data?.positionCode}</MenuItem> */}
          <MenuItem value={'QB'}>{'QB'}</MenuItem>
        </Select>
      </div>

      <div className={classes.summaryRow}>
        <div className={classes.summaryRowInner}>
          {/* Team Valuation */}
          <div className={classes.summaryBlock}>
            <div className={classes.summaryTitle}>Team Valuation</div>
            <div className={classes.summarySubtitle}>Confidential Internal Valuation</div>
          </div>

          <div className={classes.summaryIconsCell}>
            <div className={classes.eyeIcons}>
              <SettingsIcon fontSize="small" />
              <Tooltip title={teamValuation.description} placement='top'>
                <VisibilityIcon fontSize="small" />
              </Tooltip>
            </div>
          </div>

          <div className={classes.divider} />

          <div className={classes.summaryValueCell}>
            <div className={classes.summaryValue}>${teamValuation.valuation.toLocaleString()}</div>
          </div>
          <div className={classes.divider} />
          <div className={clsx(classes.summaryIconsCell, classes.iconCell)}>
            <span className={classes.miniScore}>{teamValuation.score.toFixed(1)}</span>
            {/* <TFPillLogoColorIcon className={classes.tfIcon} /> */}
            <img
              src={evaluationTeam?.logo247 || evaluationTeam?.logoESPN || ''}
              alt={evaluationTeam?.name || 'Team Logo'}
              width={20}
              height={20}
            />
          </div>
          <div className={classes.divider} />

          {/* TF College Valuation */}
          <div className={classes.summaryBlock}>
            <div className={classes.summaryTitle}>TF College Valuation</div>
            <div className={classes.summarySubtitle}>Tracking Football College TFVS</div>
          </div>

          <div className={classes.summaryIconsCell}>
            <div className={classes.eyeIcons}>
              <SettingsIcon fontSize="small" />
              <Tooltip title={tfCollegeValuation.description} placement='top'>
                <VisibilityIcon fontSize="small" />
              </Tooltip>
            </div>
          </div>
          <div className={classes.divider} />

          <div className={classes.summaryValueCell}>
            <div className={classes.summaryValue}>${tfCollegeValuation.valuation.toLocaleString()}</div>
          </div>
          <div className={classes.divider} />

          <div className={clsx(classes.summaryIconsCell, classes.iconCell)}>
            <span className={classes.miniScore}>{tfCollegeValuation.score.toFixed(1)}</span>
            <TFVLogoIcon className={classes.tfvIcon} />
          </div>
        </div>
      </div>

      <div className={classes.factorsHeader}>Player Factors</div>

      <div className={classes.factorsRow}>
        <Grid container spacing={0} className={classes.factorGrid}>
          {FACTOR_COLUMNS.map((column, columnIndex) => (
            <Grid item xs={12} md={4} key={columnIndex} className={classes.factorCol}>
              {column.map((field, rowIndex) => {
                const rawValue = factorMap[field.label];
                const factorDescription = factorDescriptionMap[field.label] || '';
                const numericValue = typeof rawValue === 'number' ? rawValue : 0;
                const displayValue = rawValue === null || rawValue === undefined ? '' : rawValue;

                return (
                  <div
                    className={clsx(
                      classes.factorRow,
                      rowIndex % 2 === 0 ? classes.factorRowOdd : classes.factorRowEven,
                    )}
                    key={field.key}
                  >
                    <span className={classes.factorName}>{field.label}</span>
                    <Tooltip title={factorDescription} placement='top'>
                      <InfoIcon className={classes.factorInfoIcon} />
                    </Tooltip>
                    <div className={classes.factorProgress}>
                      <ProgressLine showPercentileSign={false} value={numericValue} progressBarClassName={classes.progressBar} />
                    </div>
                    {/* <span className={classes.factorScore}>{displayValue}</span> */}
                  </div>
                );
              })}
            </Grid>
          ))}
        </Grid>
      </div>
    </div>
  );
}

